import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ExternalLink, GitBranch, GitCommit, GitPullRequest, Lock, RefreshCw, Star,
} from "lucide-react";
import api from "../lib/api";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { fmtDateTime, relTime } from "../lib/format";

const TABS = [
  { id: "branches", label: "Branches", Icon: GitBranch },
  { id: "pulls", label: "Pull Requests", Icon: GitPullRequest },
  { id: "commits", label: "Commits", Icon: GitCommit },
];

export default function ProjectGithubPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState(null);
  const [repo, setRepo] = useState(null);
  const [branches, setBranches] = useState([]);
  const [pulls, setPulls] = useState([]);
  const [commits, setCommits] = useState([]);
  const [tab, setTab] = useState("branches");
  const [prState, setPrState] = useState("open");
  const [commitBranch, setCommitBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const configured = status?.integration_status === "configured";

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/github/status`),
      ]);
      setProject(p);
      setStatus(s);
      if (s.integration_status === "configured") {
        const { data: r } = await api.get(`/projects/${id}/github/repo`);
        setRepo(r);
        setCommitBranch(r?.default_branch || null);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Failed to load project.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadBranches = useCallback(async () => {
    if (!configured) return;
    try {
      const { data } = await api.get(`/projects/${id}/github/branches`);
      setBranches(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Failed to load branches.");
    }
  }, [id, configured]);

  const loadPulls = useCallback(async () => {
    if (!configured) return;
    try {
      const { data } = await api.get(`/projects/${id}/github/pulls?state=${prState}&limit=50`);
      setPulls(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Failed to load pull requests.");
    }
  }, [id, configured, prState]);

  const loadCommits = useCallback(async () => {
    if (!configured || !commitBranch) return;
    try {
      const { data } = await api.get(`/projects/${id}/github/commits?branch=${encodeURIComponent(commitBranch)}&limit=50`);
      setCommits(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Failed to load commits.");
    }
  }, [id, configured, commitBranch]);

  useEffect(() => {
    if (!configured) return;
    if (tab === "branches") loadBranches();
    if (tab === "pulls") loadPulls();
    if (tab === "commits") loadCommits();
  }, [tab, configured, loadBranches, loadPulls, loadCommits]);

  const subtitle = useMemo(() => (
    project ? `Project · ${project.name}` : ""
  ), [project]);

  if (loading) return <TopBar title="GitHub" subtitle="Loading…" />;

  return (
    <>
      <TopBar
        title="GitHub"
        subtitle={subtitle}
        actions={
          <button className="btn small" onClick={loadMeta} data-testid="gh-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {!configured ? (
        <div className="panel">
          <div className="panel-body">
            <EmptyState
              title="GitHub is not configured for this project."
              subtitle="Go to Integrations to add a PAT with read access."
              icon={GitBranch}
            />
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Link to="/commandlinc/integrations" className="btn primary small">Open Integrations</Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          {repo ? (
            <div className="panel" style={{ marginBottom: 14 }} data-testid="gh-repo-tile">
              <div className="panel-head">
                <div>
                  <h2>
                    {repo.full_name}
                    {repo.private ? <Lock size={12} style={{ marginLeft: 6, verticalAlign: -1 }} /> : null}
                  </h2>
                  <p>{repo.description || "(no description)"}</p>
                </div>
                <a href={repo.html_url} target="_blank" rel="noreferrer" className="btn small">
                  Open on GitHub <ExternalLink size={11} />
                </a>
              </div>
              <div className="panel-body">
                <div className="grid-3">
                  <div className="wide-row"><div><b>Default branch</b><p className="mono">{repo.default_branch}</p></div></div>
                  <div className="wide-row"><div><b>Visibility</b><p>{repo.private ? "Private" : "Public"}</p></div></div>
                  <div className="wide-row"><div><b>Stars</b><p><Star size={11} style={{ verticalAlign: -1 }} /> {repo.stars ?? 0}</p></div></div>
                  <div className="wide-row"><div><b>Open issues</b><p>{repo.open_issues ?? 0}</p></div></div>
                  <div className="wide-row"><div><b>Size</b><p>{repo.size_kb ?? 0} KB</p></div></div>
                  <div className="wide-row"><div><b>Last push</b><p className="hint">{repo.pushed_at ? relTime(repo.pushed_at) : "—"}</p></div></div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="panel">
            <div className="panel-head" style={{ gap: 8 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {TABS.map(({ id: t, label, Icon }) => (
                  <button
                    key={t}
                    className={`btn small ${tab === t ? "primary" : ""}`}
                    onClick={() => setTab(t)}
                    data-testid={`gh-tab-${t}`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
              {tab === "pulls" ? (
                <select
                  value={prState}
                  onChange={(e) => setPrState(e.target.value)}
                  className="btn small"
                  data-testid="gh-pr-state"
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="all">All</option>
                </select>
              ) : null}
              {tab === "commits" && branches.length ? (
                <select
                  value={commitBranch || ""}
                  onChange={(e) => setCommitBranch(e.target.value)}
                  className="btn small"
                  data-testid="gh-commit-branch"
                >
                  {branches.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
              ) : null}
            </div>

            <div className="panel-body">
              {error ? <p className="hint" style={{ color: "#ffb4be" }}>{error}</p> : null}

              {tab === "branches" && (
                branches.length === 0
                  ? <EmptyState title="No branches yet." />
                  : branches.map((b) => (
                      <div className="wide-row" key={b.name} data-testid={`gh-branch-${b.name}`}>
                        <div>
                          <b>{b.name}</b>
                          <p className="mono" style={{ fontSize: 11 }}>{b.commit_sha?.slice(0, 12) || "—"}</p>
                        </div>
                        {b.protected ? <span className="pill amber">protected</span> : <span className="pill dim">open</span>}
                      </div>
                    ))
              )}

              {tab === "pulls" && (
                pulls.length === 0
                  ? <EmptyState title={`No ${prState} pull requests.`} icon={GitPullRequest} />
                  : pulls.map((p) => (
                      <div className="wide-row" key={p.number} data-testid={`gh-pr-${p.number}`}>
                        <div>
                          <b>#{p.number} · {p.title}</b>
                          <p className="hint">
                            <span className="mono">{p.head}</span> → <span className="mono">{p.base}</span>{" "}
                            · {p.user || "?"} · updated {relTime(p.updated_at)}
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span className={`pill ${p.state === "open" ? "green" : p.merged_at ? "purple" : "dim"}`}>
                            {p.merged_at ? "merged" : p.draft ? "draft" : p.state}
                          </span>
                          <a href={p.url} target="_blank" rel="noreferrer" className="btn small">
                            <ExternalLink size={11} />
                          </a>
                        </div>
                      </div>
                    ))
              )}

              {tab === "commits" && (
                commits.length === 0
                  ? <EmptyState title="No commits found." icon={GitCommit} />
                  : commits.map((c) => (
                      <div className="wide-row" key={c.sha} data-testid={`gh-commit-${c.sha?.slice(0, 8)}`}>
                        <div>
                          <b>{c.message}</b>
                          <p className="hint">
                            <span className="mono">{c.sha?.slice(0, 8)}</span> · {c.author || "?"} · {c.timestamp ? fmtDateTime(c.timestamp) : "—"}
                          </p>
                        </div>
                        <a href={c.url} target="_blank" rel="noreferrer" className="btn small">
                          <ExternalLink size={11} />
                        </a>
                      </div>
                    ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
